import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Settings, LogOut } from 'lucide-react';
import { useAuth } from '../../context';
import { userApi, UserProfile } from '../../services';
import './UserMenu.css';

export const UserMenu: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [imageError, setImageError] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Fetch profile for profile picture
  useEffect(() => {
    const fetchProfile = async () => {
      if (!isAuthenticated) return;
      try {
        const data = await userApi.getProfile();
        setProfile(data);
      } catch (err) {
        console.error('Error fetching profile:', err);
      }
    };

    fetchProfile();
  }, [isAuthenticated]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menu on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      console.error('Error logging out:', err);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  const initials = profile?.name
    ? profile.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U';

  return (
    <div className="user-menu" ref={menuRef}>
      <button
        className="user-menu__trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="User menu"
      >
        {profile?.profileImageUrl && !imageError ? (
          <img
            src={profile.profileImageUrl}
            alt={profile.name}
            className="user-menu__avatar"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="user-menu__avatar user-menu__avatar--placeholder">
            {initials}
          </div>
        )}
      </button>

      {isOpen && (
        <div className="user-menu__dropdown">
          {/* User Info Header */}
          <div className="user-menu__info">
            <div className="user-menu__info-avatar">
              {initials}
            </div>
            <div className="user-menu__info-text">
              <span className="user-menu__name">{profile?.name || 'User'}</span>
              <span className="user-menu__email">{user?.email}</span>
            </div>
          </div>

          {/* Menu Items */}
          <Link
            to="/profile"
            className="user-menu__item"
            onClick={() => setIsOpen(false)}
          >
            <User size={18} />
            <span>Profile</span>
          </Link>
          <Link
            to="/settings"
            className="user-menu__item"
            onClick={() => setIsOpen(false)}
          >
            <Settings size={18} />
            <span>Settings</span>
          </Link>

          <div className="user-menu__divider" />

          <button
            className="user-menu__item user-menu__item--danger"
            onClick={handleLogout}
          >
            <LogOut size={18} />
            <span>Sign Out</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
